import React, { useState, useEffect, ReactElement, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import type { Dispatch } from 'redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Paginator } from 'primereact/paginator';
import { Checkbox } from 'primereact/checkbox';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { SearchBase } from '../../../../types/common';
import Moment from 'moment';
import Button from '../../../Common/Button';
import { DATE_FORMAT, FILE_STATUSES, MOBILE_WINDOW_BREAKPOINT } from '../../../../constants/constants';
import { actions as filesActions } from '../../../../actions/filesActions';
import { actions as projectMgrDashboardActions } from '../../../../actions/projectMgrDashboardActions';
import { useDispatch } from 'react-redux';
import * as types from '../../../../constants/actionTypes';
import Notification from '../../../Common/Notification';
import { getSearchParamSortFields, getDataTableSortFields } from '../../../../helpers/sortHelper';
import LoadingIndicator from '../../../Common/LoadingIndicator/LoadingIndicator';
import Modal from '../../../Common/Modal/Modal';
import DateRangePickerWrapper from '../../../Common/DateRangePicker/DateRangePickerWrapper';
import BulkReassignDrawer from './BulkReassignDrawer';
import './ProjectFiles.scss';

interface ProjectFilesProps {
	subscriptionId: number;
	projectKey: string;
	windowWidth: number;
}

interface ProjectFilesSearchParams extends SearchBase {
	subscriptionId: number;
	projectKey: string;
	name: string;
	description: string;
	uploadDateStart: Date | null;
	uploadDateEnd: Date | null;
	locationKeys: Array<string>;
	uploadedByUserIds: Array<number>;
	fileStatusIds: Array<number>;
	assignedToUserIds: Array<number>;
	statusChangedDateStart: Date | null;
	statusChangedDateEnd: Date | null;
}

interface ProjectFilesFilters {
	locations: Array<any>;
	uploadedByUsers: Array<any>;
	fileStatuses: Array<any>;
	assignedUsers: Array<any>;
}

interface ProjectFiles {
	files: Array<any>;
	checkedRows: Array<any>;
	allRowsChecked: boolean;
	totalCount: number;
}

interface DateRange {
	start: Date | null;
	end: Date | null;
	name: string;
}

const ProjectFiles = ({ subscriptionId, projectKey, windowWidth }: ProjectFilesProps): ReactElement => {
	const [isInit, setIsInit] = useState<boolean>(true);
	const [isLoadingFilters, setIsLoadingFilters] = useState<boolean>(false);
	const [isSearching, setIsSearching] = useState<boolean>(false);
	const [searchTrigger, setSearchTrigger] = useState<boolean>(false);
	const [reloadTrigger, setReloadTrigger] = useState<boolean>(false);
	const [filters, setFilters] = useState<ProjectFilesFilters>({
		locations: [],
		uploadedByUsers: [],
		fileStatuses: [],
		assignedUsers: [],
	});
	const [searchParams, setSearchParams] = useState<ProjectFilesSearchParams>({
		subscriptionId: subscriptionId,
		projectKey: projectKey,
		name: '',
		description: '',
		uploadDateStart: null,
		uploadDateEnd: null,
		locationKeys: [],
		uploadedByUserIds: [],
		fileStatusIds: [],
		assignedToUserIds: [],
		statusChangedDateStart: null,
		statusChangedDateEnd: null,
		pageSize: 25, // Smaller default page size for table in section of PM Dashboard
		pageNumber: 1,
		sortBy: 'uploadDate', // Default sort by uploadDate desc
		sortDir: 'desc',
	});
	const [projectFiles, setProjectFiles] = useState<ProjectFiles>({
		files: [],
		checkedRows: [],
		allRowsChecked: false,
		totalCount: 0,
	});
	const [showDateRangePicker, setShowDateRangePicker] = useState<boolean>(false);
	const [isBulkReassignDrawerOpen, setIsBulkReassignDrawerOpen] = useState<boolean>(false);
	const [selectedBulkOption, setSelectedBulkOption] = useState<string>('');
	const [dateRange, setDateRange] = useState<DateRange>({
		start: null,
		end: null,
		name: '',
	});
	const [shouldResetChecked, setShouldResetChecked] = useState<boolean>(false);

	const dispatch: Dispatch = useDispatch();

	const loadProjectFilesFilters = async (request) => dispatch(projectMgrDashboardActions.loadProjectFilesFilters(request));
	const searchProjectFiles = async (request: ProjectFilesSearchParams) => dispatch(projectMgrDashboardActions.searchProjectFiles(request));
	const projectFilesBulkApprove = async (request) => dispatch(projectMgrDashboardActions.projectFilesBulkApprove(request));
	const projectFilesBulkDeny = async (request) => dispatch(projectMgrDashboardActions.projectFilesBulkDeny(request));
	const projectFilesBulkReassign = async (request) => dispatch(projectMgrDashboardActions.projectFilesBulkReassign(request));
	const projectFilesBulkDownload = async (request) => dispatch(projectMgrDashboardActions.projectFilesBulkDownload(request));
	const downloadFile = async (fileName: string) => dispatch(filesActions.download(fileName));

	const projectMgrDashboardActionCreators = useMemo(() => bindActionCreators(projectMgrDashboardActions, dispatch), [dispatch]);

	const { sortField, sortOrder } = getDataTableSortFields(searchParams);

	const bulkOperations = ['Reassign', 'Download', 'Approve', 'Not Approve'];
	const bulkOptions = bulkOperations.map((option) => ({
		label: option,
		value: option,
	}));

	// init on page load and re-init if projectKey changes
	useEffect(() => {
		const fetchData = async () => {
			try {
				await Promise.all([loadFilters(), search()]);
			} catch (error) {
				Notification('error', 'An Error Occurred', error);
			}

			setIsInit(false);
		};

		fetchData();
	}, [projectKey, reloadTrigger]);

	const loadFilters = async () => {
		try {
			setIsLoadingFilters(true);
			const request = {
				// Always set subscriptionId and projectKey to current props values
				subscriptionId: subscriptionId,
				projectKey: projectKey,
			};
			const result = await loadProjectFilesFilters(request);
			const payload = result.payload;
			if (result.type === types.LOAD_PROJECT_FILES_FILTERS_SUCCESS) {
				setFilters((prevState) => ({
					...prevState,
					locations: payload.locations ? payload.locations : [],
					uploadedByUsers: payload.uploadedByUsers ? payload.uploadedByUsers : [],
					fileStatuses: payload.fileStatuses ? payload.fileStatuses : [],
					assignedUsers: payload.assignedUsers ? payload.assignedUsers : [],
				}));
			} else if (result.type === types.LOAD_PROJECT_FILES_FILTERS_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		} finally {
			setIsLoadingFilters(false);
		}
	};

	const search = async () => {
		try {
			setIsSearching(true);

			const result = await searchProjectFiles(searchParams);
			const payload = result.payload;
			if (result.type === types.SEARCH_PROJECT_FILES_SUCCESS) {
				const files = payload.results.map((item) => {
					return { ...item, isChecked: false };
				});
				setProjectFiles((prevState) => ({
					...prevState,
					files: files,
					totalCount: payload.totalCount,
					allRowsChecked: false,
				}));
			} else if (result.type === types.SEARCH_PROJECT_FILES_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		} finally {
			setIsSearching(false);
		}
	};

	const onPageChange = (e) => {
		setSearchParams((prevState) => ({
			...prevState,
			pageNumber: e.page + 1,
		}));
		setSearchTrigger((prevState) => !prevState);
	};

	const onSort = ({ sortField, sortOrder }) => {
		const { sortBy, sortDir } = getSearchParamSortFields(sortField, sortOrder);
		if (sortBy !== searchParams.sortBy || sortDir !== searchParams.sortDir) {
			setSearchParams((prevState) => ({
				...prevState,
				sortBy: sortBy,
				sortDir: sortDir,
			}));
			setSearchTrigger((prevState) => !prevState);
		}
	};

	// Search if special trigger state changes
	useEffect(() => {
		if (!isInit) search();
	}, [searchTrigger]);

	const onMasterCheckboxToggle = () => {
		// Toggle the master checkbox to checked or unchecked
		const allRowsChecked = !projectFiles.allRowsChecked;

		// Check or uncheck all rows, based on the master checkbox's newly-toggled state
		const files = projectFiles.files.map((item) => {
			return { ...item, isChecked: allRowsChecked };
		});
		const checkedRows = allRowsChecked ? files.slice() : [];

		setProjectFiles((prevState) => ({
			...prevState,
			checkedRows: checkedRows,
			files: files,
			allRowsChecked: allRowsChecked,
		}));
	};

	const onToggleCheckedRow = (fileId) => {
		setProjectFiles((prevState) => {
			const checkedRows: Array<any> = [];

			const newFiles = prevState.files.map((item) => {
				const file = { ...item };
				if (item.id === fileId) {
					file.isChecked = !item.isChecked;
				}

				if (file.isChecked) {
					checkedRows.push(file);
				}
				return file;
			});

			// If we've manually checked all tasks, also check the master checkbox
			const allRowsChecked = newFiles.length === checkedRows.length;

			return {
				...prevState,
				checkedRows,
				files: newFiles,
				allRowsChecked,
			};
		});
	};

	const onDownloadFile = async (file) => {
		try {
			await downloadFile(file.storageName);
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	const filterChange = (e) => {
		let name = e.target.name;
		setSearchParams((prevState) => ({
			...prevState,
			[name]: e.target.value,
		}));
	};

	const onMultiSelectChange = (name, value) => {
		let ids = [];
		if (value.length > 0) {
			// get all the ids in an array
			ids = value.map((m) => m.id);
		}

		setSearchParams((prevState) => ({
			...prevState,
			[name]: ids,
		}));
		setSearchTrigger((prevState) => !prevState);
	};

	const onSearchBlur = () => {
		search();
	};

	const keyUpFilter = (e) => {
		if (e.keyCode == 13) {
			search();
		}
	};

	// remember which date range we are working with and show the modal
	// also populate any existing searchParams values for that particular range
	const onShowDateRangePicker = (rangeName: string) => {
		let start: Date | null = null;
		let end: Date | null = null;

		switch (rangeName) {
			case 'uploadDate':
				start = searchParams.uploadDateStart ? searchParams.uploadDateStart : null;
				end = searchParams.uploadDateEnd ? searchParams.uploadDateEnd : null;
				break;
			case 'statusChangedDate':
				start = searchParams.statusChangedDateStart ? searchParams.statusChangedDateStart : null;
				end = searchParams.statusChangedDateEnd ? searchParams.statusChangedDateEnd : null;
				break;
		}

		if (typeof end === 'string') {
			end = Moment.utc(Date.parse(end)).toDate();
		}
		if (typeof start === 'string') {
			start = Moment.utc(Date.parse(start)).toDate();
		}

		setShowDateRangePicker(true);
		setDateRange({
			name: rangeName,
			start: start,
			end: end,
		});
	};

	// the event that fires when a new date in the range picker is selected
	// remember these so we can add to search params if they hit the Apply button on the modal
	const onStartRangeChange = (startDate, endDate) => {
		setDateRange((prevState) => ({
			...prevState,
			start: startDate,
			end: endDate,
		}));
	};

	// the event that fires when the Apply button is clicked in a date range modal
	const onSelectDateRange = async () => {
		const newParams = {
			...searchParams,
		};

		if (dateRange.name === 'uploadDate') {
			newParams.uploadDateStart = dateRange.start;
			newParams.uploadDateEnd = dateRange.end;
		} else if (dateRange.name === 'statusChangedDate') {
			newParams.statusChangedDateStart = dateRange.start;
			newParams.statusChangedDateEnd = dateRange.end;
		}

		setShowDateRangePicker(false);
		setSearchParams(newParams);
		setSearchTrigger(true);
	};

	const onCancelDateRange = () => {
		setShowDateRangePicker(false);
	};

	const onBulkOptionChange = (e) => {
		setSelectedBulkOption(e.value);
	};

	const onBulkReassignCancel = () => {
		setIsBulkReassignDrawerOpen(false);

		if (shouldResetChecked) clearChecked();

		setShouldResetChecked(false);
	};

	const onBulkReassignComplete = async (user) => {
		setIsBulkReassignDrawerOpen(false);
		await bulkReassign(user);

		if (shouldResetChecked) clearChecked();

		setShouldResetChecked(false);
	};

	const resetBulkMultiSelectValue = () => {
		setSelectedBulkOption('');

		setProjectFiles((prevState) => ({
			...prevState,
			checkedRows: [],
			allRowsChecked: false,
		}));
	};

	const clearChecked = () => {
		// Uncheck all rows
		const files = projectFiles.files.map((item) => {
			return { ...item, isChecked: false };
		});

		setProjectFiles((prevState) => ({
			...prevState,
			checkedRows: [],
			files: files,
			allRowsChecked: false,
		}));
	};

	const applyBulkAction = async () => {
		switch (selectedBulkOption) {
			case 'Reassign':
				setIsBulkReassignDrawerOpen(true);
				break;
			case 'Download':
				await bulkDownload();
				break;
			case 'Not Approve':
				await bulkApprove(projectFiles.files.filter((file) => file.isChecked == true));
				break;
			case 'Deny':
				await bulkDeny(projectFiles.files.filter((file) => file.isChecked == true));
				break;
			default:
				return;
		}

		resetBulkMultiSelectValue();
	};

	const bulkApprove = async (files) => {
		const fileIds = files.map((file) => file.id);

		try {
			setIsSearching(true);
			const request = {
				// Always set subscriptionId and projectKey to current props values
				subscriptionId: subscriptionId,
				projectKey: projectKey,
				fileIds: fileIds,
			};

			const result = await projectFilesBulkApprove(request);
			const payload = result.payload;
			if (result.type === types.UPDATE_PROJECT_FILES_APPROVE_SUCCESS) {
				Notification('success', '', 'Files successfully approved');
				setReloadTrigger((prevState) => !prevState);
			} else if (result.type === types.UPDATE_PROJECT_FILES_APPROVE_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		} finally {
			setIsSearching(false);
		}
	};

	const bulkDeny = async (files) => {
		const fileIds = files.map((file) => file.id);

		try {
			setIsSearching(true);
			const request = {
				// Always set subscriptionId and projectKey to current props values
				subscriptionId: subscriptionId,
				projectKey: projectKey,
				fileIds: fileIds,
			};

			const result = await projectFilesBulkDeny(request);
			const payload = result.payload;
			if (result.type === types.UPDATE_PROJECT_FILES_DENY_SUCCESS) {
				Notification('success', '', 'Files successfully not approved');
				setReloadTrigger((prevState) => !prevState);
			} else if (result.type === types.UPDATE_PROJECT_FILES_DENY_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		} finally {
			setIsSearching(false);
		}
	};

	const bulkReassign = async (user) => {
		const files = projectFiles.files.filter((file) => file.isChecked == true);
		const fileIds = files.map((file) => file.id);

		try {
			setIsSearching(true);
			const request = {
				// Always set subscriptionId and projectKey to current props values
				subscriptionId: subscriptionId,
				projectKey: projectKey,
				assignedUserId: user.id,
				fileIds: fileIds,
			};

			const result = await projectFilesBulkReassign(request);
			const payload = result.payload;
			if (result.type === types.UPDATE_PROJECT_FILES_REASSIGN_SUCCESS) {
				Notification('success', '', 'Files successfully assigned to ' + user.firstName + ' ' + user.lastName);
				await Promise.all([loadFilters(), search()]);
			} else if (result.type === types.UPDATE_PROJECT_FILES_REASSIGN_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		} finally {
			setIsSearching(false);
		}
	};

	const bulkDownload = async () => {
		const files = projectFiles.files.filter((file) => file.isChecked == true);
		const fileIds = files.map((file) => file.id);

		try {
			setIsSearching(true);
			const request = {
				// Always set subscriptionId and projectKey to current props values
				subscriptionId: subscriptionId,
				projectKey: projectKey,
				fileIds: fileIds,
			};

			await projectFilesBulkDownload(request);
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		} finally {
			setIsSearching(false);
		}
	};

	const onApproveSingle = async (rowId) => {
		onToggleCheckedRow(rowId);

		await bulkApprove(projectFiles.files.filter((file) => file.id == rowId));

		clearChecked();
	};

	const onDenySingle = async (rowId) => {
		onToggleCheckedRow(rowId);

		await bulkDeny(projectFiles.files.filter((file) => file.id == rowId));

		clearChecked();
	};

	const onReassignSingle = async (rowId) => {
		onToggleCheckedRow(rowId);
		setShouldResetChecked(true);
		setIsBulkReassignDrawerOpen(true);
	};

	const nameFilterElement = (
		<div>
			<input
				type='text'
				name='name'
				className='ui-column-filter p-inputtext'
				placeholder='Filter by Name'
				size={50}
				onBlur={onSearchBlur}
				onChange={filterChange}
				onKeyUp={keyUpFilter}
				value={searchParams.name}
			/>
		</div>
	);

	const descriptionFilterElement = (
		<div>
			<input
				type='text'
				name='description'
				className='ui-column-filter p-inputtext'
				placeholder='Filter by Description'
				size={50}
				onBlur={onSearchBlur}
				onChange={filterChange}
				onKeyUp={keyUpFilter}
				value={searchParams.description}
			/>
		</div>
	);

	const uploadDateFilterElement = (
		<div>
			<Button
				type='button'
				bootstrapClass='light'
				name='startDateRangeButton'
				text={searchParams.uploadDateStart || searchParams.uploadDateEnd ? 'Edit Range' : 'Range'}
				onClick={() => onShowDateRangePicker('uploadDate')}
			/>
		</div>
	);

	const getColumnFilterMultiSelectValues = (sourceIds, columnFiltersValues) => {
		const results: Array<string> = [];
		if (sourceIds && sourceIds.length && columnFiltersValues && columnFiltersValues.length) {
			for (let i = 0; i < sourceIds.length; i++) {
				const value = columnFiltersValues.find((m) => m.id.toString() === sourceIds[i].toString());
				if (value) {
					results.push(value);
				}
			}
		}
		return results;
	};

	const displayColumns = windowWidth < MOBILE_WINDOW_BREAKPOINT ? 'none' : '';

	// because the multiselect needs an object and not just an id
	const assignedUserValues = getColumnFilterMultiSelectValues(searchParams.assignedToUserIds, filters.assignedUsers);
	const locationKeyValues = getColumnFilterMultiSelectValues(searchParams.locationKeys, filters.locations);
	const updatedByValues = getColumnFilterMultiSelectValues(searchParams.uploadedByUserIds, filters.uploadedByUsers);
	const fileStatusValues = getColumnFilterMultiSelectValues(searchParams.fileStatusIds, filters.fileStatuses);

	const locationFilterElement = (
		<MultiSelect
			className='ui-column-filter'
			optionLabel='name'
			dataKey='id'
			value={locationKeyValues}
			options={filters.locations}
			onChange={(e) => onMultiSelectChange('locationKeys', e.value)}
		/>
	);

	const uploadedUserFilterElement = (
		<MultiSelect
			className='ui-column-filter'
			optionLabel='name'
			dataKey='id'
			value={updatedByValues}
			options={filters.uploadedByUsers}
			onChange={(e) => onMultiSelectChange('uploadedByUserIds', e.value)}
		/>
	);

	const approvingUserFilterElement = (
		<MultiSelect
			className='ui-column-filter'
			optionLabel='name'
			dataKey='id'
			value={assignedUserValues}
			options={filters.assignedUsers}
			onChange={(e) => onMultiSelectChange('assignedToUserIds', e.value)}
		/>
	);

	const fileStatusFilterElement = (
		<MultiSelect
			className='ui-column-filter'
			optionLabel='name'
			dataKey='id'
			value={fileStatusValues}
			options={filters.fileStatuses}
			onChange={(e) => onMultiSelectChange('fileStatusIds', e.value)}
		/>
	);

	const fileStatusModifiedDateFilterElement = (
		<Button
			type='button'
			bootstrapClass='light'
			name='statusChangedDateRangeButton'
			text={searchParams.statusChangedDateStart || searchParams.statusChangedDateEnd ? 'Edit Range' : 'Range'}
			onClick={() => onShowDateRangePicker('statusChangedDate')}
		/>
	);

	const masterCheckbox = <Checkbox name='checkbox' onChange={() => onMasterCheckboxToggle()} checked={projectFiles.allRowsChecked}></Checkbox>;

	const checkboxTemplate = (rowData) => {
		return <Checkbox name='checkbox' onChange={() => onToggleCheckedRow(rowData.id)} checked={rowData.isChecked}></Checkbox>;
	};

	const nameTemplate = (rowData) => {
		return (
			<div className='file-name' onClick={() => onDownloadFile(rowData)}>
				{rowData.name}
			</div>
		);
	};

	const uploadedUserTemplate = (rowData) => {
		return (
			<div className='uploaded-user'>
				<div className='user-name'>{rowData.uploadedUserFullName}</div>
				<div className='user-jobtitle'>{rowData.uploadedUserJobTitle}</div>
			</div>
		);
	};

	const approvingUserTemplate = (rowData) => {
		const actionIcons = (
			<div className='action-icons'>
				<i className='far fa-user-pen' title='Reassign' onClick={() => onReassignSingle(rowData.id)} />
				<i className='far fa-cloud-arrow-down' title='Download' onClick={() => onDownloadFile(rowData)} />
				<i className='far fa-circle-check' title='Approve' onClick={() => onApproveSingle(rowData.id)} />
				<i className='far fa-times-circle' title='Not Approve' onClick={() => onDenySingle(rowData.id)} />
			</div>
		);

		if (!rowData.fileStatusModifiedByUserId || !rowData.reviewedByUserName) {
			return <div className='reviewer-actions'>{actionIcons}</div>;
		} else {
			return (
				<>
					<div className='reviewer-name'>
						<div>{rowData.reviewedByUserName}</div>
					</div>
					<div className='reviewer-actions'>{actionIcons}</div>
				</>
			);
		}
	};

	const uploadDateTemplate = (rowData) => {
		if (rowData.uploadDate) {
			return Moment.utc(rowData.uploadDate).format(DATE_FORMAT);
		} else {
			return null;
		}
	};

	const fileStatusTemplate = (rowData) => {
		return (
			<div className='file-status'>
				{rowData.fileStatusId === FILE_STATUSES.Approved ? (
					<div className='status-icon approved'></div>
				) : rowData.fileStatusId === FILE_STATUSES.Denied ? (
					<div className='status-icon denied'></div>
				) : (
					<div className='status-icon pending'></div>
				)}

				<span className='file-status-name'>{rowData.fileStatusName}</span>
			</div>
		);
	};

	const statusDateTemplate = (rowData) => {
		if (rowData.fileStatusModifiedDate) {
			return Moment.utc(rowData.fileStatusModifiedDate).format(DATE_FORMAT);
		} else {
			return null;
		}
	};

	const header = (
		<div className='table-header-container'>
			<div className='row'>
				<div className='col-md-12'>
					<div className='btn-group'>
						<Dropdown
							className='ui-column-filter'
							value={selectedBulkOption}
							options={bulkOptions}
							onChange={onBulkOptionChange}
							style={{
								width: '250px',
								fontFamily: 'GreyCliff',
								borderRadius: '6px',
								fontSize: '14px',
							}}
							placeholder='Bulk Actions'
						/>
						<button
							type='button'
							className='btn pacer61-purple-btn ms-3'
							disabled={projectFiles.checkedRows.length === 0}
							onClick={applyBulkAction}>
							Apply
						</button>
					</div>
				</div>
			</div>
		</div>
	);

	return (
		<div className='project-file-section-ex'>
			<LoadingIndicator isLoading={isLoadingFilters || isSearching} hideWhileLoading={true}>
				<div className='identifier-results-table'>
					<DataTable
						filterDisplay='row'
						className='p-datatable-sm p-datatable-striped'
						header={header}
						value={projectFiles.files}
						sortField={sortField}
						sortOrder={sortOrder}
						onSort={onSort}>
						<Column
							className='checkbox-column'
							field='isChecked'
							filter={true}
							filterElement={masterCheckbox}
							body={checkboxTemplate}
							style={{ textAlign: 'center', width: '40px' }}
						/>
						<Column
							field='fileStatusName'
							header='File Status'
							sortable={true}
							filter={true}
							filterElement={fileStatusFilterElement}
							body={fileStatusTemplate}
							style={{ width: '125px', display: displayColumns }}
						/>
						<Column
							field='fileStatusModifiedDate'
							header='Status Date'
							sortable={true}
							filter={true}
							filterElement={fileStatusModifiedDateFilterElement}
							body={statusDateTemplate}
							style={{ width: '105px', display: displayColumns }}
						/>
						<Column field='name' header='File Name' sortable={true} body={nameTemplate} filter={true} filterElement={nameFilterElement} />
						<Column
							field='description'
							header='Description'
							sortable={true}
							filter={true}
							filterElement={descriptionFilterElement}
							style={{ display: displayColumns }}
						/>
						<Column
							field='uploadDate'
							header='Upload Date'
							sortable={true}
							filter={true}
							filterElement={uploadDateFilterElement}
							body={uploadDateTemplate}
							style={{ width: '115px' }}
						/>
						<Column
							field='locationName'
							header='Location/Team'
							sortable={true}
							filter={true}
							filterElement={locationFilterElement}
							style={{ display: displayColumns }}
						/>
						<Column
							field='uploadedUserFullName'
							header='Uploaded By'
							sortable={true}
							filter={true}
							filterElement={uploadedUserFilterElement}
							body={uploadedUserTemplate}
							style={{ display: displayColumns }}
						/>
						<Column
							field='reviewedByUserName'
							header='File Reviewer'
							sortable={true}
							filter={true}
							filterElement={approvingUserFilterElement}
							body={approvingUserTemplate}
							style={{ display: displayColumns }}
						/>
					</DataTable>
					{projectFiles.totalCount > searchParams.pageSize && (
						<Paginator
							rows={searchParams.pageSize}
							totalRecords={projectFiles.totalCount}
							first={(searchParams.pageNumber - 1) * searchParams.pageSize}
							onPageChange={onPageChange}
						/>
					)}
					<div className='results-count'>Total records: {projectFiles.totalCount}</div>
				</div>
			</LoadingIndicator>
			{showDateRangePicker && (
				<Modal
					id='dateRangeModal'
					title='Select Date Range'
					body={
						<div className='datepicker-container'>
							<DateRangePickerWrapper
								onDatesChange={onStartRangeChange}
								initialStartDate={dateRange.start}
								initialEndDate={dateRange.end}
							/>
						</div>
					}
					primaryButtonText='Apply'
					onPrimaryClick={onSelectDateRange}
					onSecondaryClick={onCancelDateRange}
					onCloseClick={onCancelDateRange}
				/>
			)}
			{isBulkReassignDrawerOpen && (
				<BulkReassignDrawer
					subscriptionId={subscriptionId}
					projectKey={projectKey}
					projectMgrDashboardActions={projectMgrDashboardActionCreators}
					onComplete={onBulkReassignComplete}
					onCancel={onBulkReassignCancel}
				/>
			)}
		</div>
	);
};

export default ProjectFiles;
